/**
 * Tag Date Modal - Set expiration and goes-live dates for individual tags
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, CalendarClock, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const TagDateModal = ({ isOpen, onClose, tag, onSuccess }) => {
  const [expiresAt, setExpiresAt] = useState('');
  const [goesLiveAt, setGoesLiveAt] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && tag) {
      // Pre-fill with existing dates
      setExpiresAt(tag.expires_at ? tag.expires_at.split('T')[0] : '');
      setGoesLiveAt(tag.goes_live_at ? tag.goes_live_at.slice(0, 16) : '');
    }
  }, [isOpen, tag]);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const payload = {
        nfc_codes: [tag.nfc_code],
        expires_at: expiresAt ? new Date(expiresAt + 'T23:59:59Z').toISOString() : null,
        goes_live_at: goesLiveAt ? new Date(goesLiveAt).toISOString() : null
      };

      await axios.post(`${API}/api/admin/tags/bulk-expiration`, payload);
      
      toast.success('Dates updated successfully');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to update dates:', error);
      toast.error(error.response?.data?.detail || 'Failed to update dates');
    } finally {
      setSaving(false);
    }
  };

  const handleClearDates = async () => {
    try {
      setSaving(true);
      
      const payload = {
        nfc_codes: [tag.nfc_code],
        expires_at: null,
        goes_live_at: null
      };

      await axios.post(`${API}/api/admin/tags/bulk-expiration`, payload);
      
      setExpiresAt('');
      setGoesLiveAt('');
      toast.success('Dates cleared');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to clear dates:', error);
      toast.error('Failed to clear dates');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !tag) return null;

  const hasContent = tag.message || tag.description || (tag.media_files && tag.media_files.length > 0);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-background rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-primary" />
                <div>
                  <h2 className="text-xl font-semibold">Set Dates</h2>
                  <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                    {tag.description || tag.nfc_code}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
                data-testid="date-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Warning for empty tags */}
            {!hasContent && (
              <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">Empty Tag</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      This tag has no content yet. Dates will still be set but won't have any visible effect until content is added.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Expiration Date */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-500" />
                Expiration Date
              </label>
              <p className="text-xs text-muted-foreground mb-3">
                Content becomes unavailable after this date
              </p>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="expiration-date-input"
              />
              {expiresAt && (
                <div className="flex items-center gap-2 mt-2 text-sm text-orange-600">
                  <Clock className="w-4 h-4" />
                  <span>Expires: {new Date(expiresAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* Goes Live Date */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-blue-500" />
                Goes Live Date
              </label>
              <p className="text-xs text-muted-foreground mb-3">
                Content becomes visible at this date/time
              </p>
              <input
                type="datetime-local"
                value={goesLiveAt}
                onChange={(e) => setGoesLiveAt(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="goes-live-input"
              />
              {goesLiveAt && (
                <div className="flex items-center gap-2 mt-2 text-sm text-blue-600">
                  <CalendarClock className="w-4 h-4" />
                  <span>Goes live: {new Date(goesLiveAt).toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Current Status */}
            {(tag.expires_at || tag.goes_live_at) && (
              <div className="p-4 rounded-xl bg-secondary/50">
                <p className="text-sm font-medium mb-2">Current Settings</p>
                <div className="space-y-1 text-sm text-muted-foreground">
                  {tag.expires_at && (
                    <p>Expires: {new Date(tag.expires_at).toLocaleDateString()}</p>
                  )}
                  {tag.goes_live_at && (
                    <p>Goes live: {new Date(tag.goes_live_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-border flex justify-between gap-3">
            <button
              onClick={handleClearDates}
              disabled={saving || (!tag.expires_at && !tag.goes_live_at && !expiresAt && !goesLiveAt)}
              className="px-4 py-2.5 rounded-full text-orange-600 hover:bg-orange-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="clear-dates-btn"
            >
              Clear Dates
            </button>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-full bg-secondary font-medium"
                data-testid="date-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="date-modal-save"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TagDateModal;
